import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../master-utilities.module.scss";
import uploadDocumentIcon from "../../../../images/document-download.svg";
import Tooltip from "../../../ui-kit/tooltip/tooltip";
import {
  removeUploadedFileAndGeometry,
  setBuildingTerritoryGeometry,
  setFileHidden,
  setSelectedFeature,
  setStageTerritoryGeometry,
  toggleHiddenTerritoryObject,
} from "../../../../services/reducers/utilities-journal";
import {
  checkStageTerritoryFileIntersectionAsync,
  removeAllTerritory,
  removeTerritoryObject,
  setConvertedFeatures,
  setFileLayerVisibility,
  setObjectVisibility,
} from "../../../../services/actions/map";
import {
  hasValidBuildingPermitForStage,
  isGeometryContained,
  validateConfirmStageTerritory,
} from "../../../../utils/helpers-for-utilities";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import ImportFileModal from "../import-file-modal/import-file-modal";
import VerifiedConfirmTerritoryModal from "../verified-confirm-territory-modal/verified-confirm-territory-modal";
import { useLock } from "../useLock";
import { StageType } from "../../../../interfaces/utilities";
import { stopDrawingTools } from "../../../../services/reducers/map";
import { getStageFiles } from "../../../../services/api";

type TerritoryType = "stage" | "building";

//TODO Нужно упростить, когда будет больше времени
const UploadFilesSection = () => {
  const { t } = useTranslation();
  const {
    selectedRow,
    uploadedFiles,
    selectedFeature,
    stageTerritoryGeometry,
    buildingTerritoryGeometry,
    isLoadingProjectFiles,
    fileHidden,
    hiddenTerritories,
    filesLoadedFromServer,
  } = useAppSelector((state) => state.masterUtilities);
  const { layers } = useAppSelector((state) => state.map);

  const [stageTerritoryError, setStageTerritoryError] = useState(false);
  const [buildingTerritoryError, setBuildingTerritoryError] = useState(false);
  const [stageTooltipShown, setStageTooltipShown] = useState(false);
  const [buildingTooltipShown, setBuildingTooltipShown] = useState(false);
  const [isVisibleImportFileModal, setVisibleImportFileModal] =
    useState<StageType | null>(null);
  const [isVerifiedConfirmTerritory, setVerifiedConfirmTerritory] =
    useState<TerritoryType | null>(null);
  const [
    isVisibleVerifiedConfirmTerritory,
    setIsVisibleVerifiedConfirmTerritory,
  ] = useState<boolean>(false);
  const [stageFileIntersectionError, setStageFileIntersectionError] =
    useState<boolean>(false);

  const dispatch = useAppDispatch();

  const isAsBuiltStage = selectedRow?.stage === "As-Built";

  const designFilesLoaded =
    filesLoadedFromServer.design.loaded && filesLoadedFromServer.design.stageId;
  const asBuiltFilesLoaded =
    filesLoadedFromServer.asBuilt.loaded &&
    filesLoadedFromServer.asBuilt.stageId;

  useEffect(() => {
    if (!isAsBuiltStage || isLoadingProjectFiles) return;

    if (uploadedFiles.design.length > 0) {
      uploadedFiles.design.forEach(({ name }) => {
        dispatch(
          setFileLayerVisibility({
            fileName: name,
            visible: false,
            stage: "design",
          }),
        );
      });

      const hiddenDesign = Object.fromEntries(
        uploadedFiles.design.map(({ name }) => [name, true]),
      );

      Object.entries(hiddenDesign).forEach(([fileName, hidden]) => {
        dispatch(setFileHidden({ stage: "design", fileName, hidden }));
      });
    }

    if (stageTerritoryGeometry.design) {
      handleVisibleStageTerritory("design");
    }

    if (buildingTerritoryGeometry.design) {
      handleVisibleBuildingTerritory("design");
    }
  }, [isLoadingProjectFiles]);

  const openImportModal = (stage: StageType) => {
    setVisibleImportFileModal(stage);
  };
  const closeImportModal = () => setVisibleImportFileModal(null);

  const handleDeleteFile = (name: string, stage: StageType) => {
    dispatch(removeUploadedFileAndGeometry({ stage, name }));
    dispatch(setConvertedFeatures());

    const remainingFiles = uploadedFiles[stage].filter((f) => f.name !== name);

    if (remainingFiles.length === 0) {
      dispatch(removeAllTerritory({ stage }));

      if (
        selectedFeature?.graphic === stageTerritoryGeometry[stage] ||
        selectedFeature?.graphic === buildingTerritoryGeometry[stage]
      ) {
        dispatch(setSelectedFeature(null));
      }
    }

    dispatch(stopDrawingTools());
  };

  const handleConfirmStageTerritory = async (stage: StageType) => {
    setStageTooltipShown(false);
    if (selectedFeature?.isArea && selectedRow) {
      const isValid = await validateConfirmStageTerritory(
        layers,
        selectedFeature?.graphic,
        selectedRow,
      );

      if (!isValid) {
        setStageTerritoryError(true);
        setVerifiedConfirmTerritory("stage");
        setIsVisibleVerifiedConfirmTerritory(true);
        return;
      }

      setStageTerritoryError(false);

      const intersectsWithFileObjects =
        await checkStageTerritoryFileIntersectionAsync(
          dispatch,
          selectedFeature.graphic,
          stage,
        );

      setStageFileIntersectionError(!intersectsWithFileObjects);

      if (!intersectsWithFileObjects) {
        setStageTerritoryError(true);
        return;
      }

      dispatch(
        setStageTerritoryGeometry({
          stageType: stage,
          geometry: selectedFeature.graphic,
        }),
      );
    } else {
      setStageTerritoryError(true);
    }
  };

  const handleCancelStageTerritory = (stage: StageType) => {
    const stageObjectId = stageTerritoryGeometry[stage]?.attributes.uuid;

    if (stageObjectId) {
      dispatch(removeTerritoryObject({ objectId: stageObjectId }));
    }

    dispatch(setStageTerritoryGeometry({ stageType: stage, geometry: null }));
    if (buildingTerritoryGeometry[stage]) {
      const buildingObjectId =
        buildingTerritoryGeometry[stage]?.attributes?.OBJECTID ??
        buildingTerritoryGeometry[stage]?.attributes?.uuid;

      if (buildingObjectId) {
        dispatch(removeTerritoryObject({ objectId: buildingObjectId }));
      }

      dispatch(
        setBuildingTerritoryGeometry({ stageType: stage, geometry: null }),
      );
    }
    if (selectedFeature?.graphic === stageTerritoryGeometry[stage]) {
      setSelectedFeature(null);
    }
  };

  const handleConfirmBuildingTerritory = (stage: StageType) => {
    if (selectedFeature?.isArea && selectedRow) {
      const isValid = isGeometryContained(
        stageTerritoryGeometry[stage],
        selectedFeature?.graphic,
      );
      if (isValid) {
        dispatch(
          setBuildingTerritoryGeometry({
            stageType: stage,
            geometry: selectedFeature.graphic,
          }),
        );
        setBuildingTerritoryError(false);
      } else {
        setVerifiedConfirmTerritory("building");
        setIsVisibleVerifiedConfirmTerritory(true);
      }
    } else {
      setBuildingTerritoryError(true);
    }
  };

  const handleCancelBuildingTerritory = (stage: StageType) => {
    const buildingObjectId = buildingTerritoryGeometry[stage]?.attributes?.uuid;

    if (buildingObjectId) {
      dispatch(removeTerritoryObject({ objectId: buildingObjectId }));
    }

    dispatch(
      setBuildingTerritoryGeometry({ stageType: stage, geometry: null }),
    );
    if (selectedFeature?.graphic === buildingTerritoryGeometry[stage]) {
      setSelectedFeature(null);
    }
  };

  const handleOneTimeTooltipShow = (
    error: boolean,
    tooltipShown: boolean,
    setTooltipShown: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    if (error && !tooltipShown) {
      setTooltipShown(true);
    }
  };

  const handleVisibleStageTerritory = (stage: StageType) => {
    dispatch(toggleHiddenTerritoryObject({ type: "stage", stage }));

    const isHidden = hiddenTerritories.stage[stage];
    const uuid = stageTerritoryGeometry[stage]?.attributes?.uuid;
    if (uuid) {
      dispatch(setObjectVisibility(uuid, isHidden));
    }
  };

  const handleVisibleBuildingTerritory = (stage: StageType) => {
    dispatch(toggleHiddenTerritoryObject({ type: "building", stage }));

    const isHidden = hiddenTerritories.building[stage];
    const uuid = buildingTerritoryGeometry[stage]?.attributes?.uuid;
    if (uuid) {
      dispatch(setObjectVisibility(uuid, isHidden));
    }
  };

  const toggleFile = (stage: StageType, fileName: string) => {
    const isHidden = fileHidden[stage]?.[fileName] ?? false;
    const nextHidden = !isHidden;

    dispatch(setFileHidden({ stage, fileName, hidden: nextHidden }));

    dispatch(setFileLayerVisibility({ fileName, visible: !nextHidden, stage }));
  };

  const lock = useLock();

  const handleDownloadStageFiles = async (stageId: number) => {
    try {
      await getStageFiles(stageId);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <div className={styles.fileUploadSection}>
        <div className={styles.fileUploadLine}>
          <span
            className={`${styles.label} ${lock?.designStageImport && styles.disabled}`}
          >
            {t("master-utilities.designStageFiles")}
          </span>
          <div className={styles.groupBtn}>
            {designFilesLoaded && (
              <button
                onClick={() =>
                  handleDownloadStageFiles(
                    filesLoadedFromServer.design.stageId!,
                  )
                }
                className={styles.btn}
              >
                <div className={`${styles.downloadIcon} ${styles.icon}`} />
                {t("master-utilities.downloadFile")}
              </button>
            )}
            <button
              disabled={lock?.designStageImport}
              onClick={() => openImportModal("design")}
              className={styles.btn}
            >
              <div className={`${styles.importIcon} ${styles.icon}`} />
              {t("master-utilities.importFile")}
            </button>
          </div>
        </div>
        {uploadedFiles.design.length > 0 && (
          <>
            <ul className={styles.uploadedFilesList}>
              {uploadedFiles.design.map((file, index) => (
                <li key={index} className={styles.uploadedFileItem}>
                  <img
                    className={`${styles.uploadDocIcon} ${
                      fileHidden.design[file.name] ? styles.hidden : ""
                    }`}
                    src={uploadDocumentIcon}
                    alt="uploadDocument"
                  />
                  <span
                    onClick={() => toggleFile("design", file.name)}
                    className={`${styles.uploadedFileName} ${
                      fileHidden.design[file.name] ? styles.hidden : ""
                    }`}
                  >
                    {file.name}.{file.extension}
                  </span>
                  {!lock?.hideDeleteFilesDesign && (
                    <button
                      className={styles.deleteFileBtn}
                      onClick={() => handleDeleteFile(file.name, "design")}
                    >
                      <span className={styles.deleteFileIcon} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className={styles.territoryDialog}>
              {!stageTerritoryGeometry.design ? (
                <div className={styles.territoryWrapper}>
                  <span className={styles.territoryText}>
                    {t("master-utilities.drawWorkArea")}
                  </span>
                  <div className={styles.btnGroup}>
                    <Tooltip
                      disabled={stageTooltipShown || !stageTerritoryError}
                      position="bottom"
                      text={
                        stageFileIntersectionError
                          ? t("master-utilities.selectIntersectingPolygon")
                          : t("master-utilities.selectAnyPolygon")
                      }
                    >
                      <button
                        disabled={!!stageTerritoryGeometry.design}
                        onClick={() => handleConfirmStageTerritory("design")}
                        className={styles.confirmBtn}
                        onMouseEnter={() =>
                          handleOneTimeTooltipShow(
                            stageTerritoryError,
                            stageTooltipShown,
                            setStageTooltipShown,
                          )
                        }
                      >
                        <div className={styles.confirmIcon} />
                        {t("master-utilities.confirmTerritory")}
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ) : (
                <div className={styles.territoryWrapper}>
                  <span className={`${styles.territoryText} ${styles.bold}`}>
                    {t("master-utilities.workArea")}
                  </span>
                  <div className={styles.btnGroup}>
                    <button
                      className={styles.confirmBtn}
                      onClick={() => handleVisibleStageTerritory("design")}
                    >
                      <div
                        className={
                          !hiddenTerritories.stage.design
                            ? styles.showIcon
                            : styles.hideIcon
                        }
                      />
                      {!hiddenTerritories.stage.design
                        ? t("master-utilities.hideOnMap")
                        : t("master-utilities.showOnMap")}
                    </button>
                    {!lock?.hideDeleteTerritoryDesign && (
                      <button
                        className={styles.cancelBtn}
                        onClick={() => handleCancelStageTerritory("design")}
                      >
                        <div className={styles.deleteIcon} />
                        {t("master-utilities.deleteTerritory")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {stageTerritoryGeometry.design && (
              <div className={styles.territoryDialog}>
                {!buildingTerritoryGeometry.design &&
                hasValidBuildingPermitForStage(selectedRow, "design") ? (
                  <div className={styles.territoryWrapper}>
                    <span className={styles.territoryText}>
                      {t("master-utilities.drawBuildingArea")}
                    </span>
                    <div className={styles.btnGroup}>
                      <Tooltip
                        disabled={
                          buildingTooltipShown || !buildingTerritoryError
                        }
                        delay={0}
                        position="bottom"
                        text={
                          stageFileIntersectionError
                            ? t("master-utilities.selectIntersectingPolygon")
                            : t("master-utilities.selectAnyPolygon")
                        }
                      >
                        <button
                          disabled={!!buildingTerritoryGeometry.design}
                          onClick={() =>
                            handleConfirmBuildingTerritory("design")
                          }
                          className={styles.confirmBtn}
                          onMouseEnter={() =>
                            handleOneTimeTooltipShow(
                              buildingTerritoryError,
                              buildingTooltipShown,
                              setBuildingTooltipShown,
                            )
                          }
                        >
                          <div className={styles.confirmIcon} />
                          {t("master-utilities.confirmTerritory")}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ) : buildingTerritoryGeometry.design ? (
                  <div className={styles.territoryWrapper}>
                    <span className={`${styles.territoryText} ${styles.bold}`}>
                      {t("master-utilities.buildingArea")}
                    </span>
                    <div className={styles.btnGroup}>
                      <button
                        className={styles.confirmBtn}
                        onClick={() => handleVisibleBuildingTerritory("design")}
                      >
                        <div
                          className={
                            !hiddenTerritories.building.design
                              ? styles.showIcon
                              : styles.hideIcon
                          }
                        />
                        {!hiddenTerritories.building.design
                          ? t("master-utilities.hideOnMap")
                          : t("master-utilities.showOnMap")}
                      </button>
                      {!lock?.hideDeleteTerritoryDesign && (
                        <button
                          className={styles.cancelBtn}
                          onClick={() =>
                            handleCancelBuildingTerritory("design")
                          }
                        >
                          <div className={styles.deleteIcon} />
                          {t("master-utilities.deleteTerritory")}
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}

        {/* ASBUILT FILES */}
        <div className={styles.fileUploadLine}>
          <span
            className={`${styles.label} ${lock?.asBuiltStageImport && styles.disabled}`}
          >
            {t("master-utilities.asBuiltStageFiles")}
          </span>
          <div className={styles.groupBtn}>
            {asBuiltFilesLoaded && (
              <button
                onClick={() =>
                  handleDownloadStageFiles(
                    filesLoadedFromServer.asBuilt.stageId!,
                  )
                }
                className={styles.btn}
              >
                <div className={`${styles.downloadIcon} ${styles.icon}`} />
                {t("master-utilities.downloadFile")}
              </button>
            )}
            <button
              disabled={lock?.asBuiltStageImport}
              className={styles.btn}
              onClick={() => openImportModal("asBuilt")}
            >
              <div className={`${styles.importIcon} ${styles.icon}`} />
              {t("master-utilities.importFile")}
            </button>
          </div>
        </div>
        {uploadedFiles.asBuilt.length > 0 && (
          <>
            <ul className={styles.uploadedFilesList}>
              {uploadedFiles.asBuilt.map((file, index) => (
                <li key={index} className={styles.uploadedFileItem}>
                  <img
                    className={`${styles.uploadDocIcon} ${
                      fileHidden.asBuilt[file.name] ? styles.hidden : ""
                    }`}
                    src={uploadDocumentIcon}
                    alt="uploadDocument"
                  />
                  <span
                    className={`${styles.uploadedFileName} ${
                      fileHidden.asBuilt[file.name] ? styles.hidden : ""
                    }`}
                    onClick={() => toggleFile("asBuilt", file.name)}
                  >
                    {file.name}.{file.extension}
                  </span>
                  {!lock?.hideDeleteFilesAsBuilt && (
                    <button
                      className={styles.deleteFileBtn}
                      onClick={() => handleDeleteFile(file.name, "asBuilt")}
                    >
                      <span className={styles.deleteFileIcon} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className={styles.territoryDialog}>
              {!stageTerritoryGeometry.asBuilt ? (
                <div className={styles.territoryWrapper}>
                  <span className={styles.territoryText}>
                    {t("master-utilities.drawWorkArea")}
                  </span>
                  <div className={styles.btnGroup}>
                    <Tooltip
                      disabled={stageTooltipShown || !stageTerritoryError}
                      position="bottom"
                      text={t("master-utilities.selectAnyPolygon")}
                    >
                      <button
                        disabled={!!stageTerritoryGeometry.asBuilt}
                        onClick={() => handleConfirmStageTerritory("asBuilt")}
                        className={styles.confirmBtn}
                        onMouseEnter={() =>
                          handleOneTimeTooltipShow(
                            stageTerritoryError,
                            stageTooltipShown,
                            setStageTooltipShown,
                          )
                        }
                      >
                        <div className={styles.confirmIcon} />
                        {t("master-utilities.confirmTerritory")}
                      </button>
                    </Tooltip>
                  </div>
                </div>
              ) : (
                <div className={styles.territoryWrapper}>
                  <span className={`${styles.territoryText} ${styles.bold}`}>
                    {t("master-utilities.workArea")}
                  </span>
                  <div className={styles.btnGroup}>
                    <button
                      className={styles.confirmBtn}
                      onClick={() => handleVisibleStageTerritory("asBuilt")}
                    >
                      <div
                        className={
                          !hiddenTerritories.stage.asBuilt
                            ? styles.showIcon
                            : styles.hideIcon
                        }
                      />
                      {!hiddenTerritories.stage.asBuilt
                        ? t("master-utilities.hideOnMap")
                        : t("master-utilities.showOnMap")}
                    </button>
                    {!lock?.hideDeleteFilesAsBuilt && (
                      <button
                        className={styles.cancelBtn}
                        onClick={() => handleCancelStageTerritory("asBuilt")}
                      >
                        <div className={styles.deleteIcon} />
                        {t("master-utilities.deleteTerritory")}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            {stageTerritoryGeometry.asBuilt && (
              <div className={styles.territoryDialog}>
                {!buildingTerritoryGeometry.asBuilt &&
                hasValidBuildingPermitForStage(selectedRow, "asBuilt") ? (
                  <div className={styles.territoryWrapper}>
                    <span className={styles.territoryText}>
                      {t("master-utilities.drawBuildingArea")}
                    </span>
                    <div className={styles.btnGroup}>
                      <Tooltip
                        disabled={
                          buildingTooltipShown || !buildingTerritoryError
                        }
                        delay={0}
                        position="bottom"
                        text={t("master-utilities.selectAnyPolygon")}
                      >
                        <button
                          disabled={!!buildingTerritoryGeometry.asBuilt}
                          onClick={() =>
                            handleConfirmBuildingTerritory("asBuilt")
                          }
                          className={styles.confirmBtn}
                          onMouseEnter={() =>
                            handleOneTimeTooltipShow(
                              buildingTerritoryError,
                              buildingTooltipShown,
                              setBuildingTooltipShown,
                            )
                          }
                        >
                          <div className={styles.confirmIcon} />
                          {t("master-utilities.confirmTerritory")}
                        </button>
                      </Tooltip>
                    </div>
                  </div>
                ) : buildingTerritoryGeometry.asBuilt ? (
                  <div className={styles.territoryWrapper}>
                    <span className={`${styles.territoryText} ${styles.bold}`}>
                      {t("master-utilities.buildingArea")}
                    </span>
                    <div className={styles.btnGroup}>
                      <button
                        className={styles.confirmBtn}
                        onClick={() =>
                          handleVisibleBuildingTerritory("asBuilt")
                        }
                      >
                        <div
                          className={
                            !hiddenTerritories.building.asBuilt
                              ? styles.showIcon
                              : styles.hideIcon
                          }
                        />
                        {!hiddenTerritories.building.asBuilt
                          ? t("master-utilities.hideOnMap")
                          : t("master-utilities.showOnMap")}
                      </button>
                      {!lock?.hideDeleteFilesAsBuilt && (
                        <button
                          className={styles.cancelBtn}
                          onClick={() =>
                            handleCancelBuildingTerritory("asBuilt")
                          }
                        >
                          <div className={styles.deleteIcon} />
                          {t("master-utilities.deleteTerritory")}
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>

      {isVisibleImportFileModal && (
        <ImportFileModal
          stage={isVisibleImportFileModal}
          onClose={closeImportModal}
        />
      )}

      {isVisibleVerifiedConfirmTerritory && isVerifiedConfirmTerritory && (
        <VerifiedConfirmTerritoryModal
          onClose={() => setIsVisibleVerifiedConfirmTerritory(false)}
          type={isVerifiedConfirmTerritory}
        />
      )}
    </>
  );
};

export default UploadFilesSection;
